import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    NgZone,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Params, Router } from '@angular/router';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SsoFeature } from 'src/environments/features/sso.feature';

import { ModalController, Platform, ToastController } from '@ionic/angular';
import { isEmpty } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { DiversityCheckModal } from './diversity-check/diversity-check.modal';
import { ExternalClientStylesService } from './external-client-styles/external-client-styles.service';
import { PerformanceCycleService } from './performance-cycles/performance-cycles.service';
import { AttributesService } from './processes/attributes.service';
import { SystemNotificationPaged } from './shared/classes/notification';
import { DiversityService } from './shared/diversity.service';
import { MenuPagesInterface } from './shared/interfaces/menu-pages.interface';
import { UserPreview } from './shared/interfaces/user-preview.interface';
import { NotificationModalComponent } from './shared/modals/notification-modal/notification-modal.component';
import { AuthService, LoginResponse, UserItem } from './shared/services/auth.service';
import { applyCachedExternalClientStyles, applyExternalClientStyles, ExternalClientsStylesCacheKey } from './shared/services/external-client.service';
import { LogoService } from './shared/services/logo.service';
import { NotificationService } from './shared/services/notification.service';
import { SharedService } from './shared/services/shared.service';
import { TranslationService } from './shared/services/translation.service';
import { ThemeService } from './shared/services/multi-clients/theme.service';
import { AssetService } from './shared/services/multi-clients/asset.service';
import { ConfigService } from './shared/services/multi-clients/config.service';
import { UrlService } from './shared/services/url.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html', styles: [
        `
            ion-router-outlet.impersonating-bottom {
                margin-bottom: 55px;
            }
            
            #main-content {
                position: relative;
                height: 100vh;
                overflow: hidden;
            }
            
            ion-header {
                position: relative;
                z-index: 10;
            }
            
            ion-router-outlet {
                position: absolute;
                top: 56px; /* altura padrão do ion-header */
                left: 0;
                right: 0;
                bottom: 0;
                overflow: auto;
            }
            
            /* Garantir que o conteúdo interno não seja cortado */
            ion-router-outlet ion-page {
                height: 100%;
                overflow: auto;
            }
            
            ion-router-outlet ion-content {
                --height: 100%;
                height: 100%;
            }
            
            /* Ajuste para quando há toolbar de impersonation */
            /*ion-router-outlet.impersonating-bottom {
                top: 111px; /* altura do header + toolbar de impersonation
                bottom: 0;
            }*/
        `,
    ],
})
export class AppComponent implements OnDestroy, AfterViewInit, OnInit {

    public appPages: MenuPagesInterface[] = [
        {
            title: 'dashboard.viewTeamProgress',
            url: '/performance-cycles',
        }
    ];
    public selectedLanguage: string = 'pt-BR';
    private _languageSubscription: Subscription;
    public showLoader: boolean;
    private _loaderSubscription: Subscription;
    public logoUrl = '';
    public hideReports: boolean;
    public showNotificationsFeature: boolean;
    public cannotOpenProfile;
    public principlesConfigurationEnabled;

    public currentUser: UserItem;
    public styles: any;
    public notifications: SystemNotificationPaged = { lastPage: 0, unreadCount: 0, notifications: [] };
    public notificationsNotRead: number = 0;
    public userRoles: Array<string> = [];
    public passwordChangeRequired: boolean;
    public isImpersonating: boolean = false;
    public impersonatedInfo: UserPreview;
    public ssoWithRedirect: boolean;
    public overrideLogin: boolean;
    public isHrOrHasReportAccess: boolean;

    private _userSubscription: Subscription;
    private _impersonationSubscription: Subscription;
    private _params: {} & Params;
    public showNotification: boolean = false;
    public tutorialPath: string = 'assets/documents/tutorial.pdf';
    sso: import("src/environments/features/sso.feature").SsoFeature;
    tutorialMenu: boolean;
    private _isHrOrAdmin: boolean = false;

    constructor(
        private platform: Platform,
        private _translationService: TranslationService,
        private _authService: AuthService,
        private _sharedService: SharedService,
        private _diversityService: DiversityService,
        private _router: Router,
        private modalController: ModalController,
        private zone: NgZone,
        private _cookieService: CookieService,
        protected _toastController: ToastController,
        protected _performanceCycleService: PerformanceCycleService,
        private _logoService: LogoService,
        private _notificationService: NotificationService,
        private cdr: ChangeDetectorRef,
        private _externalClientStylesService: ExternalClientStylesService,
        private _attributeService: AttributesService,
        private _assetService: AssetService, private _configService: ConfigService, private _themeService: ThemeService,) {
        // Inicialização movida para ngOnInit
    }

    ngOnInit(): void {
        console.log('🚀 DEBUG: ngOnInit iniciado');

        // Aguardar as configurações carregarem através do APP_INITIALIZER
        this.initializeSettings();
        this.initializeApp();

        console.log('✅ DEBUG: ngOnInit concluído');
    }

    private initializeSettings() {
        this.logoUrl = this._assetService.getLogoPath();

        this._logoService.logoSubject.subscribe(logo => {
            this.logoUrl = logo ? logo : this.logoUrl;
        });
        const clientName = this._configService.settings.clientName;
        //Feature flags
        this.sso = this._configService.settings.features.sso;
        this.hideReports = this._configService.settings.hideReports;
        this.showNotificationsFeature = this._configService.settings.features.showNotifications;
        this.tutorialMenu = this._configService.settings.features.tutorialMenu;
        this._translationService.initializeLanguageSettings();

        this.cannotOpenProfile = this._configService.settings.features.cannotOpenProfile;
        this.principlesConfigurationEnabled = this._configService.settings.features.principlesConfiguration;

        this._themeService.loadTheme(clientName);
        this._assetService.loadAssets(clientName);

        this._assetService.getTutorialPdfPath().then(path => {
            this.tutorialPath = path;
        });
    }

    private initializeDefaultSettings() {
        console.warn('⚠️  Usando configurações padrão devido a falha no carregamento');
        const clientName = 'btg'; // default
        //Feature flags padrão
        this.sso = new SsoFeature({
            enabled: false,
            logoutUrl: '',
            logoutByClient: false
        });
        this.hideReports = false;
        this.showNotificationsFeature = true;
        this.tutorialMenu = true;
        this._translationService.initializeLanguageSettings();

        this.cannotOpenProfile = false;
        this.principlesConfigurationEnabled = false;

        this._themeService.loadTheme(clientName);
        this._assetService.loadAssets(clientName);

        this._assetService.getTutorialPdfPath().then(path => {
            this.tutorialPath = path;
        });
    }

    initializeApp() {
        this.platform.ready().then(async () => {
            if (this.platform.is('capacitor')) {
                await StatusBar.setStyle({ style: Style.Default });
                await SplashScreen.hide();
            }

            let currentNavigation = this._router.getCurrentNavigation();
            if (currentNavigation && currentNavigation.extractedUrl) {
                let params = await this._router.getCurrentNavigation().extractedUrl.queryParams;
                if (params && params.pdfExporting === 'true')
                    localStorage.setItem('pdfExporting', 'true');
            } this._validateAuth();

            const sLanguage = this._translationService.getStoredLanguage();
            this.selectedLanguage = sLanguage || this.selectedLanguage;

            this._languageSubscription = this._translationService.languageChanged$.subscribe(
                (language) => (this.selectedLanguage = language)
            );

            this._setMenuItems();

            this._authService.passwordChangeRequired().subscribe((required) => {
                this.passwordChangeRequired = required;
            });

            this.impersonatedInfo = this._authService.getImpersonatedUser();
            this.isImpersonating = this.impersonatedInfo && true;

            this._impersonationSubscription = this._authService.personificationChanged$.subscribe(
                (isImpersonating) => {
                    this.isImpersonating = isImpersonating;
                    this.impersonatedInfo = this._authService.getImpersonatedUser();
                });

            if (this.platform.is('capacitor')) {
                App.addListener('appUrlOpen', (data: any) => {
                    this.zone.run(() => {
                        if (data.url.includes('/login')) return;
                        const slug = data.url.split('.com').pop();
                        if (slug) {
                            this._router.navigateByUrl(slug);
                        }
                    });
                });
            }
        });
    } private _validateAuth() {
        console.log('🔐 DEBUG: _validateAuth iniciado');
        const params = new URLSearchParams(window.location.search);

        if (this.platform.is('capacitor')) {
            let hasToken = this._authService.hasToken();
            console.log('📱 DEBUG: Capacitor - hasToken:', hasToken);

            if (hasToken) {
                if (this._params && this._params.FirstAccess && this._params.FirstAccess === 'True') {
                    this._router.navigate(['first-access']);
                } else if (window.location.pathname === '/') {
                    this._router.navigate(['dashboard']);
                }
            } else {
                this._router.navigate(['login']);
            }
            return;
        }


        const SSO_ERROR_KEY = 'ssoerror';
        const SSOErrorMessage = this._cookieService.get(SSO_ERROR_KEY);
        const errorInSsoResponse = this._params && this._params['ssoerror'] || SSOErrorMessage;

        this._params = this._urlSearchParamsToObject(params);
        this.ssoWithRedirect = this.sso.redirect.enabled;

        let hasToken = this._authService.hasToken();

        const SSO_SUCCESS_KEY = 'ssosuccess';
        this.overrideLogin = this._params['overrideLogin'] === '1';
        if (this.sso.enabled) {
            const authenticationInfoToParse = this._cookieService.get(SSO_SUCCESS_KEY);

            if (this._params && this._params[SSO_SUCCESS_KEY]) {
                this._authService.ssoLogin(this._params);
                hasToken = this._authService.hasToken();
            } else if (authenticationInfoToParse) {
                const tokenResource: {
                    firstAccess,
                    accessToken,
                    username,
                    userId,
                    name,
                    roles
                } = (JSON.parse(authenticationInfoToParse) || {})['tokenResource'];
                if (!tokenResource) {
                    this._toastController.create({ message: 'login.noUserInfo', color: 'danger' });
                    return;
                }
                this._authService.ssoLogin(tokenResource);
                this._cookieService.delete(SSO_SUCCESS_KEY, '/', undefined);
                hasToken = this._authService.hasToken();
            }
        }
        if (hasToken) {
            if (this._params.FirstAccess && this._params.FirstAccess === 'True') {
                this._router.navigate(['first-access']);
            } else if (window.location.pathname === '/') {
                this._router.navigate(['dashboard']);
            }
        } else if (errorInSsoResponse) {
            const translatedError = this._translationService.getTranslation(
                this._params[SSO_ERROR_KEY] || SSOErrorMessage
            );

            this._toastController.create({ message: translatedError, color: 'danger' })
                .then(toast => {
                    toast.present();
                    location.replace(this.sso.logoutUrl);
                });
            if (SSOErrorMessage) {
                this._cookieService.delete(SSO_ERROR_KEY, '/', undefined);
            }
        } else if (!hasToken) {
            if (this.ssoWithRedirect && !this.overrideLogin) {
                // Redirecionar para o endpoint SSO
                window.location.href = this.sso.redirect.endpoint;
            } else {
                // Redirecionar para a tela de login
                const params = this.overrideLogin ? { queryParams: { overrideLogin: 1 } } : {};
                const path = window.location.pathname.replace('/', '');
                const isRouteValid = !isEmpty(path);
                this._router.navigate([isRouteValid ? path : '/login'], params);
            }
        }
    } private _setMenuItems() {
        // console.log('📋 DEBUG: _setMenuItems iniciado');
        this._authService.isLoggedIn().subscribe((isLoggedIn) => {
            // console.log('🔐 DEBUG: isLoggedIn:', isLoggedIn);
            if (isLoggedIn) {
                console.log('👤 DEBUG: Usuário logado, buscando dados...');
                this._authService.getUserData().subscribe((response) => {
                    // console.log('📊 DEBUG: getUserData response:', response);
                    this.currentUser = response.data;

                    // Debug das condições do menu
                    // console.log('🔍 DEBUG: currentUser:', this.currentUser);
                    // console.log('🔍 DEBUG: currentUser.manager:', this.currentUser?.manager);
                    // console.log('🔍 DEBUG: isHrOrHasReportAccess:', this.isHrOrHasReportAccess);
                    // console.log('🔍 DEBUG: passwordChangeRequired:', this.passwordChangeRequired);
                    // console.log('🔍 DEBUG: isAdmin():', this.isAdmin());

                    const shouldShowMenu = (this.currentUser && (this.currentUser.manager || this.isHrOrHasReportAccess) && !this.passwordChangeRequired) || this.isAdmin();
                    console.log('🔍 DEBUG: shouldShowMenu:', shouldShowMenu);

                    this.initExternalClientStyles();
                    this.userRoles = this._authService.getUserRoles();
                    this.IsHrOrHasReportAccess();
                    this._hasHrOrAdmin();

                    // Forçar detecção de mudanças
                    this.cdr.detectChanges();

                    let parameters: MenuPagesInterface = {
                        title: 'generic.parameters',
                        url: '',
                        children: []
                    };

                    if (this.userRoles.includes('Administrator')) {
                        this.appPages.splice(1, 0, {
                            title: 'generic.performanceProcesses',
                            url: '/processes',
                        });
                    }

                    if (this.userRoles.includes('UserManagement')) {
                        this.appPages.splice(1, 0, {
                            title: 'Processo de Sincronia',
                            url: '/usuarios/processo-de-sincronia',
                        });
                    }

                    if (this.userRoles.includes('UserManagement')) {
                        this.appPages.push({
                            title: 'generic.userManagement',
                            url: '/users/admin',
                        });
                    }

                    if (this.userRoles.includes('VisibilityManagement')) {
                        this.appPages.push({
                            title: 'generic.visibilityManagement',
                            url: '/users/visibility',
                        });
                    }

                    if (
                        this.userRoles.includes('Administrator') ||
                        this.userRoles.includes('OKRAdmin')
                    ) {
                        this.appPages.push({
                            title: 'generic.OKRAdmin',
                            url: '/okr',
                        });
                    }

                    if (this.userRoles.includes('Administrator') ||
                        this.userRoles.includes('HR')) {
                        this.appPages.push({
                            title: 'Feedbacks',
                            url: '/feedback',
                        });
                    }

                    if (this.userRoles.includes('Administrator') ||
                        this.userRoles.includes('HR')) {
                        this.appPages.push({
                            title: 'ExternalEvaluation',
                            url: '/external-evaluations',
                        });
                    }

                    if (!this.hideReports ||
                        this.userRoles.includes('Administrator') ||
                        this.userRoles.includes('HR') ||
                        this.userRoles.includes('ReportAccess')) {
                        this.appPages.push({
                            title: 'generic.reports',
                            url: '/reports',
                        });
                    }

                    if (this.userRoles.includes('AI-Review')) {
                        parameters.children.push(
                            {
                                title: 'generic.reviews',
                                url: '/parameters/reviews-config'
                            }
                        );
                    }

                    if (this.principlesConfigurationEnabled && this._isHrOrAdmin) {
                        parameters.children.push(
                            {
                                title: 'generic.principlesConfiguration',
                                url: '/parameters/principles-config'
                            }
                        );
                    }

                    if (parameters && parameters.children && parameters.children.length > 0) {
                        this.appPages.push(parameters);
                    }

                    this._diversityService
                        .getLatestDiversityCheck()
                        .subscribe((latestDiversityCheck) => {
                            if (latestDiversityCheck.data.needsToAccept) {
                                this._createDiversityModal().then((diversityModal) => {
                                    diversityModal.onDidDismiss().then((dismissEvent) => {
                                        if (!dismissEvent.data) this._authService.logout();
                                    });
                                    diversityModal.present();
                                });
                            }
                        });
                    this.getNotifications();
                });
            } else {
                this.currentUser = null;
                this.userRoles = [];
                this.appPages = [
                    {
                        title: 'dashboard.viewTeamProgress',
                        url: '/performance-cycles',
                    }
                ];
            }
        });
    }

    private _urlSearchParamsToObject(searchParams) {
        const object = {};
        for (const [key, value] of searchParams.entries()) {
            if (!object.hasOwnProperty(key)) {
                object[key] = value;
            } else {
                // If the key already exists, convert the value to an array
                // and push the new value to the array
                if (!Array.isArray(object[key])) {
                    object[key] = [object[key]];
                }
                object[key].push(value);
            }
        }
        return object;
    }

    public IsHrOrHasReportAccess() {
        this.isHrOrHasReportAccess = this.userRoles && (this.userRoles.includes('HR') || this.userRoles.includes('ReportAccess'));
    }

    private _hasHrOrAdmin() {
        this._isHrOrAdmin = this.userRoles && (this.userRoles.includes('HR') || this.userRoles.includes('ReportAccess'));
    }

    public isAdmin() {
        if (this.userRoles.includes('Administrator') || this.userRoles.includes('OKRAdmin')) {
            return true;
        }
        return false;
    }

    private async _createDiversityModal() {
        return await this.modalController.create({
            component: DiversityCheckModal,
            cssClass: 'diversity-check-modal',
            componentProps: { accepted: false },
            backdropDismiss: false,
        });
    } ngAfterViewInit() {
        // OAuth2Client já está disponível diretamente na versão 5.0.0+

        setTimeout(() => {
            this._loaderSubscription = this._sharedService
                .getLoaderSubject()
                .subscribe((value: boolean) => {
                    this.showLoader = value;
                    this.cdr.detectChanges();  // <<<<<< Aqui força a atualização segura
                });
        }, 500);
    }

    public goToDashboard(): void {
        if (!this.passwordChangeRequired) this._router.navigate(['dashboard']);
    }

    openProfile() {
        if (!this.cannotOpenProfile) {
            this._router.navigate(['profile']);
        }
    }

    public leaveImpersonation(): void {
        this._performanceCycleService.leaveImpersonation().subscribe(res => {
            if (res.success) {
                let response = res.data;
                this._authService.clearImpersonationInfo();
                let actualAuth = this._authService.getLoggedUser();
                let newAuth: LoginResponse = {
                    'Name': undefined,
                    'UserId': undefined,
                    'completed_registration': undefined,
                    'email_verified': undefined,
                    'first_access': undefined,
                    'imageUrl': undefined,
                    'refresh_token': undefined,
                    'role': undefined,
                    'roles': actualAuth.roles,
                    'sso': undefined,
                    'access_token': response.tokenResource.accessToken,
                    'username': actualAuth.username,
                    'user_id': actualAuth.user_id,
                    'name': actualAuth.name,
                    'expires_in': undefined,
                    'country': undefined
                };

                this._authService.setLoggedUser(newAuth).then(_ => {
                    this._router.navigate(['performance-cycles']);
                    setTimeout(() => {
                        window.location.reload();
                    });
                });
                this.getNotifications();
                this.initExternalClientStyles();

            }
        }, error => {
            throw new Error(error.message);
        });
    }

    public getUserName() {



        let impersonateUser: UserItem | null;
        impersonateUser = localStorage.getItem('ImpersonatedUser') ? JSON.parse(localStorage.getItem('ImpersonatedUser')) : null;
        return impersonateUser && impersonateUser.username
            ? impersonateUser.username
            : (this.currentUser && this.currentUser.username
                ? this.currentUser.username
                : '');
    }

    public getUserPicture() {
        var impersonateUser: UserItem | null;
        impersonateUser = localStorage.getItem('ImpersonatedUser') ? JSON.parse(localStorage.getItem('ImpersonatedUser')) : null;
        return impersonateUser && impersonateUser.imageUrl
            ? impersonateUser.imageUrl
            : (this.currentUser && this.currentUser.imageUrl
                ? this.currentUser.imageUrl
                : 'assets/imgs/user-image-placeholder.png');
    }

    public async changeLanguage(language: string): Promise<void> {
        await this._translationService.changeSelectedLanguage(language);
    }

    public logOut(): void {
        this._authService.logout();
    }

    public getNotifications() {
        if (!this.showNotificationsFeature) {
            console.log('Notifications feature is disabled');
            return;
        }
        this._notificationService.getNotifications().subscribe((response) => {
            this.notifications = response.data;
            this.notificationsNotRead = this.notifications.unreadCount;
        });
    }

    public initExternalClientStyles() {
        applyExternalClientStyles(
            this._attributeService,
            this._externalClientStylesService,
            this._logoService,
            this._authService,
            this.currentUser ? this.currentUser.id : null
        );
    }

    public initCachedExternalClientStyles() {
        this.impersonatedInfo = this._authService.getImpersonatedUser();
        applyCachedExternalClientStyles(
            this._logoService,
            this._authService,
            this.impersonatedInfo?.userId ?? this.currentUser?.id
        );
    }

    public async openNotificationModal() {
        const modalRef = await this.modalController.create({
            component: NotificationModalComponent,
            // cssClass: 'diversity-check-modal',
            componentProps: { notifications: this.notifications },
            // backdropDismiss: false,
        });

        await modalRef.present();
        modalRef.onDidDismiss().then((value) => {
            if (value.data.updatedMessage) {
                this.getNotifications();
            }
        })

    }

    ngOnDestroy() {
        this._languageSubscription.unsubscribe();
        this._userSubscription.unsubscribe();
        this._impersonationSubscription.unsubscribe();
        this._loaderSubscription.unsubscribe();
    }
}